/**
 * Parsed, validated environment.
 *
 * Read once at startup and validated, so a typo in a deploy variable produces a clear
 * failure instead of a blank dashboard and a console full of 404s.
 *
 * Everything here is public by definition — it is compiled into the bundle. No secret is
 * ever read on this side; the browser authenticates with an httpOnly cookie it cannot read.
 */

type AppEnv = 'local' | 'staging' | 'production'

function readString(value: unknown, fallback: string): string {
  return typeof value === 'string' && value.length > 0 ? value : fallback
}

function readInt(value: unknown, fallback: number): number {
  const parsed = Number.parseInt(String(value ?? ''), 10)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback
}

/**
 * An empty base URL is the intended default: the app calls same-origin `/api`, which is
 * how it runs behind a single ingress. That also means one built image can be promoted
 * across environments instead of being rebuilt per environment.
 */
const baseUrl = readString(import.meta.env.VITE_API_BASE_URL, '').replace(/\/+$/, '')

export const env = {
  apiBaseUrl: baseUrl,
  appEnv: readString(import.meta.env.VITE_APP_ENV, 'local') as AppEnv,
  chatEnabled: readString(import.meta.env.VITE_ENABLE_CHAT, 'true') !== 'false',
  pollIntervalMs: readInt(import.meta.env.VITE_POLL_INTERVAL_MS, 15_000),
  /**
   * Who is publishing this deployment, and where to write about it.
   *
   * The privacy policy and terms are real documents that name a real operator — Google's
   * OAuth review reads them — so they are configured per deployment rather than hardcoded.
   * A fork that changes nothing else still gets a policy naming the right people.
   */
  legal: {
    operator: readString(import.meta.env.VITE_LEGAL_OPERATOR, 'this deployment’s operator'),
    contactEmail: readString(import.meta.env.VITE_LEGAL_CONTACT_EMAIL, ''),
    updatedAt: readString(import.meta.env.VITE_LEGAL_UPDATED_AT, '20 September 2026'),
  },
} as const

export const isProduction = env.appEnv === 'production'
